import type { HexMapState } from '../engine/render/renderer';
import { getColdestWarmthRatio, getPopulationCount } from '../engine/map/settlement';
import { computeFoodHoursLeft } from '../engine/settlement/computeFoodHoursLeft';
import { t } from '../i18n/translations';

export function runSettlementAlerts(state: HexMapState): boolean {
    let changed = false;
    const now = state.simulation.tick;
    const { warningCooldownTicks, starvationHours, coldDebtRatio } = state.config.alerts;

    Object.values(state.settlementsById).forEach((settlement) => {
        const pop = getPopulationCount(settlement);
        if (pop > 0) {
            const hoursSupply = computeFoodHoursLeft(settlement);
            const lastStarveTick = settlement.production.population.lastStarvationWarningTick;
            if (
                hoursSupply <= starvationHours &&
                (lastStarveTick < 0 || now - lastStarveTick >= warningCooldownTicks)
            ) {
                pushAlert(
                    state,
                    t(
                        state.config.ui.language,
                        'overlay.alertStarvationWarning',
                        settlement.name,
                        hoursSupply,
                    ),
                    'warn',
                );
                settlement.production.population.lastStarvationWarningTick = now;
                changed = true;
            }

            const ratio = getColdestWarmthRatio(settlement);
            const lastColdTick = settlement.production.population.lastColdWarningTick;
            if (
                ratio >= coldDebtRatio &&
                (lastColdTick < 0 || now - lastColdTick >= warningCooldownTicks)
            ) {
                pushAlert(
                    state,
                    t(
                        state.config.ui.language,
                        'overlay.alertColdWarning',
                        settlement.name,
                        Math.round(ratio * 100),
                    ),
                    'warn',
                );
                settlement.production.population.lastColdWarningTick = now;
                changed = true;
            }
        }

        if (settlement.production.population.lastStarvationDeaths > 0) {
            pushAlert(
                state,
                t(
                    state.config.ui.language,
                    'overlay.alertDeaths',
                    settlement.production.population.lastStarvationDeaths,
                    settlement.name,
                    t(state.config.ui.language, 'overlay.alertCauseStarvation'),
                ),
                'error',
            );
            settlement.production.population.lastStarvationDeaths = 0;
            changed = true;
        }

        if (settlement.production.population.lastColdDeaths > 0) {
            pushAlert(
                state,
                t(
                    state.config.ui.language,
                    'overlay.alertDeaths',
                    settlement.production.population.lastColdDeaths,
                    settlement.name,
                    t(state.config.ui.language, 'overlay.alertCauseCold'),
                ),
                'error',
            );
            settlement.production.population.lastColdDeaths = 0;
            changed = true;
        }
    });

    return changed;
}

function pushAlert(state: HexMapState, message: string, level: 'warn' | 'error'): void {
    state.alertIdCounter += 1;
    state.alerts.push({ id: `alert_${state.alertIdCounter}`, message, level });
}
